// Composição de providers: Redux → ErrorBoundary global → ToastProvider → children.
// ErrorBoundary global é o último recurso; cada feature tem seu próprio
// (<InboxBoundary>, etc.) — readme §13.
import type { ReactNode } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import { store } from '../store';
import { ErrorFallback } from '@/components/layout/ErrorFallback';
import { ToastProvider } from '@/components/ui/Toast';
import { ThemeProvider } from '@/lib/theme';
import { attachInboxWsListeners } from '@/features/inbox';
import { attachDashboardRealtimeListener } from '@/features/dashboard';
import { attachQueueWsListener } from '@/features/queue';
import { attachStorePresenceListener } from '@/features/stores';
import { attachNotificationsWsListener, attachNotificationSoundListener } from '@/features/notifications';
import { unlockAudio } from '@/lib/sound/alerts';
import { attachPresenceHeartbeat } from '@/lib/ws/presence';
import { attachWsConnection } from '@/lib/ws/connection';

// Registra listeners de WS por feature uma única vez no boot. listenerMiddleware
// é additivo — chamar várias vezes é seguro mas evitamos por higiene.
attachInboxWsListeners();
// Eventos de ciclo de vida de conversa (/inbox) invalidam os contadores
// "Tempo real" do dashboard (refetch debounced, só quando montado).
attachDashboardRealtimeListener();
// Eventos de ciclo de vida de conversa (/inbox) invalidam a Fila ao vivo —
// lead novo de WhatsApp aparece sem refresh; card sai quando alguém assume.
attachQueueWsListener();
// presence.changed (/inbox) → patcha o status online dos cards da aba Equipe na
// hora, sem refetch. Substitui o antigo polling de 30s do TeamTab.
attachStorePresenceListener();
// message.received (/inbox, INBOUND) → marca conversa como não-lida pro badge do
// Sidebar quando ela não está aberta. Zera por conversa ao abri-la.
attachNotificationsWsListener();
// Alertas sonoros: bipe distinto pra mensagem minha e pra conversa nova na fila.
// unlockAudio arma o resume() do AudioContext no primeiro gesto (política de
// autoplay); depois disso toca mesmo com a aba sem foco.
attachNotificationSoundListener();
unlockAudio();
// Conecta o socket /inbox quando o user autentica (e desconecta no logout) —
// sem isto o gateway nunca marca presença e o heartbeat vira no-op.
attachWsConnection(store);
// Heartbeat de presença: cliente emite presence:ping a cada 30s no /inbox
// pra manter o user na lista de online (§7 backend Onda 3).
attachPresenceHeartbeat(store);

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <ThemeProvider>
      <ReduxProvider store={store}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ToastProvider>{children}</ToastProvider>
        </ErrorBoundary>
      </ReduxProvider>
    </ThemeProvider>
  );
}
